import { GraphQLClient } from 'graphql-request';
import { nanoid } from 'nanoid';

import type { RHFGroupFieldValue } from '@/components/dynamic-filter/FilterFormGroup';
import clientApi from '@/libs/axios/instance.client';
import { getRoleType } from '@/utils/role';
import type { Role } from '@/utils/role/role';

export interface QueryOptions {
    graphqlQuery: (
        entity: string,
        fields: any,
        additionalWhere?: Record<string, RHFGroupFieldValue>
    ) => string;
}

let refreshPromise: Promise<boolean> | null = null;

function getBasePath(): string {
    const bp = (process.env.NEXT_PUBLIC_BASE_PATH || '').trim();

    if (!bp) return '';

    return bp.startsWith('/') ? bp : `/${bp}`;
}

function buildSameOriginUrl(path: string): string {
    const bp = getBasePath();
    const p = `${bp}${path.startsWith('/') ? path : `/${path}`}`;

    if (typeof window === 'undefined') return p; // should not happen in client services

    return new URL(p, window.location.origin).toString();
}

async function ensureRefreshedOnce(): Promise<boolean> {
    if (!refreshPromise) {
        refreshPromise = fetch(buildSameOriginUrl('/api/auth/refresh'), {
            method: 'POST',
            credentials: 'include',
            cache: 'no-store',
        })
            .then(r => r.ok)
            .catch(() => false)
            .finally(() => {
                refreshPromise = null;
            });
    }

    return refreshPromise;
}

function extractGqlCode(err: unknown): string | null {
    try {
        const anyErr = err as any;

        return anyErr?.response?.errors?.[0]?.extensions?.code ?? null;
    } catch {
        return null;
    }
}

function isUnauthenticated(code: string | null): boolean {
    return code === 'UNAUTHORIZED';
}

function isForbidden(code: string | null): boolean {
    return code === 'FORBIDDEN';
}

export abstract class BaseService {
    public readonly graphQLUrl: string;

    protected readonly gqlClient: GraphQLClient;

    protected takeRecords = 10;

    protected endpoints = {};

    constructor() {
        this.graphQLUrl = buildSameOriginUrl('/api/graphql');

        this.gqlClient = new GraphQLClient(this.graphQLUrl, {
            credentials: 'include',
        });
    }

    protected async fetchGqlList<T = any>(
        query: string,
        entityName: string
    ): Promise<T> {
        const { getQueryClient } = await import('@/libs/react-query');

        const qc = getQueryClient();

        try {
            const result = await this.gqlClient.request<any>(query);

            return result?.[entityName] as T;
        } catch (error) {
            const code = extractGqlCode(error);

            // Token hết hạn → refresh
            if (isUnauthenticated(code)) {
                const ok = await ensureRefreshedOnce();

                if (!ok) {
                    throw new Error(
                        'Phiên đăng nhập hết hạn, vui lòng đăng nhập lại'
                    );
                }

                const result = await this.gqlClient.request<any>(query);

                qc.invalidateQueries();

                return result?.[entityName] as T;
            }

            // Không đủ quyền → KHÔNG refresh
            if (isForbidden(code)) {
                throw new Error(
                    'Bạn không có quyền, vui lòng đăng xuất hoặc liên hệ admin để được hỗ trợ.'
                );
            }

            throw error;
        }
    }

    public getEndpointList() {
        return this.endpoints;
    }

    public getAPIRequestInstance() {
        return clientApi;
    }

    public getSearchField(
        value = '',
        fieldSearch: string | string[] = ''
    ): Record<string, RHFGroupFieldValue> {
        const initFilter = { ...this.getFilterByRole() };

        if (Array.isArray(fieldSearch)) {
            const id = nanoid();

            initFilter[id] = {
                id,
                inputType: 'input',
                key: fieldSearch,
                operator: 'like',
                value,
            };
        } else {
            initFilter['getSearchField'] = {
                id: 'getSearchField',
                inputType: 'input',
                key: fieldSearch || this.getFieldSearch(),
                operator: 'like',
                value,
            };
        }

        return initFilter;
    }

    public getFieldSearch(): string | string[] {
        return 'name';
    }

    public getRoleTypeManager(): Role {
        return (getRoleType() || {}) as Role;
    }

    public getQueryById() {
        return this.getRoleTypeManager()?.queryById() as string;
    }

    public abstract getFilterByRole(): Record<string, RHFGroupFieldValue>;

    /**
     * Display a listing of the resource.
     *
     * @return Response
     */
    public abstract index(query: string): Promise<any>;

    public abstract getFields(): Record<string, any>;

    public abstract getEntityName(): string;

    public additionalWhere(): Record<string, RHFGroupFieldValue> {
        return {};
    }
}
